import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Add token to requests
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Properties API
export const propertiesApi = {
  getAll: (params = {}) => axios.get(`${API}/properties`, { params, headers: getAuthHeaders() }),
  getById: (id) => axios.get(`${API}/properties/${id}`, { headers: getAuthHeaders() }),
  create: (data) => axios.post(`${API}/properties`, data, { headers: getAuthHeaders() }),
  update: (id, data) => axios.put(`${API}/properties/${id}`, data, { headers: getAuthHeaders() }),
  delete: (id) => axios.delete(`${API}/properties/${id}`, { headers: getAuthHeaders() }),
  getMine: () => axios.get(`${API}/my-properties`, { headers: getAuthHeaders() }),
  getReviews: (id) => axios.get(`${API}/properties/${id}/reviews`, { headers: getAuthHeaders() })
};

// Bookings API
export const bookingsApi = {
  create: (data) => axios.post(`${API}/bookings`, data, { headers: getAuthHeaders() }),
  getAll: (params = {}) => axios.get(`${API}/bookings`, { params, headers: getAuthHeaders() }),
  updateStatus: (id, status) => axios.put(`${API}/bookings/${id}/status`, { status }, { headers: getAuthHeaders() })
};

// Messages API
export const messagesApi = {
  send: (data) => axios.post(`${API}/messages`, data, { headers: getAuthHeaders() }),
  getConversations: () => axios.get(`${API}/conversations`, { headers: getAuthHeaders() }),
  getMessages: (conversationId) => axios.get(`${API}/conversations/${conversationId}/messages`, { headers: getAuthHeaders() })
};

// Reviews API
export const reviewsApi = {
  create: (data) => axios.post(`${API}/reviews`, data, { headers: getAuthHeaders() })
};

// Payments API
export const paymentsApi = {
  createCheckout: (bookingId, originUrl, includeDepositHold = false) => 
    axios.post(`${API}/payments/create-checkout`, { 
      booking_id: bookingId, 
      origin_url: originUrl,
      include_deposit_hold: includeDepositHold 
    }, { headers: getAuthHeaders() }),
  getStatus: (sessionId) => axios.get(`${API}/payments/status/${sessionId}`, { headers: getAuthHeaders() }),
  getSettings: () => axios.get(`${API}/payments/settings`, { headers: getAuthHeaders() }),
  getHistory: () => axios.get(`${API}/payments/history`, { headers: getAuthHeaders() }),
  // Stripe Connect
  startConnectOnboarding: (originUrl) => 
    axios.post(`${API}/payments/connect/onboard`, { origin_url: originUrl }, { headers: getAuthHeaders() }),
  getConnectStatus: () => axios.get(`${API}/payments/connect/status`, { headers: getAuthHeaders() }),
  getConnectDashboard: () => axios.get(`${API}/payments/connect/dashboard`, { headers: getAuthHeaders() }),
  // Deposit holds
  createDepositHold: (bookingId) => 
    axios.post(`${API}/payments/deposit-hold`, { booking_id: bookingId }, { headers: getAuthHeaders() }),
  captureDeposit: (depositId, amount = null) => 
    axios.post(`${API}/payments/deposit/${depositId}/capture`, { amount }, { headers: getAuthHeaders() }),
  releaseDeposit: (depositId) => 
    axios.post(`${API}/payments/deposit/${depositId}/release`, {}, { headers: getAuthHeaders() }),
  // Refunds
  createRefund: (transactionId, amount = null, reason = 'requested_by_customer') => 
    axios.post(`${API}/payments/refund`, { transaction_id: transactionId, amount, reason }, { headers: getAuthHeaders() })
};

// Saved Properties API
export const savedApi = {
  toggle: (propertyId) => axios.post(`${API}/saved-properties/${propertyId}`, {}, { headers: getAuthHeaders() }),
  getAll: () => axios.get(`${API}/saved-properties`, { headers: getAuthHeaders() }),
  check: (propertyId) => axios.get(`${API}/saved-properties/check/${propertyId}`, { headers: getAuthHeaders() })
};

// Users API
export const usersApi = {
  getProfile: (userId) => axios.get(`${API}/users/${userId}`, { headers: getAuthHeaders() })
};

// Subscriptions API
export const subscriptionsApi = {
  getTiers: () => axios.get(`${API}/subscriptions/tiers`),
  getMySubscription: () => axios.get(`${API}/subscriptions/me`, { headers: getAuthHeaders() }),
  createCheckout: (tier, originUrl) => 
    axios.post(`${API}/subscriptions/checkout`, { tier, origin_url: originUrl }, { headers: getAuthHeaders() }),
  getCheckoutStatus: (sessionId) => 
    axios.get(`${API}/subscriptions/checkout/status/${sessionId}`, { headers: getAuthHeaders() }),
  cancel: (cancelImmediately = false) => 
    axios.post(`${API}/subscriptions/cancel`, { cancel_immediately: cancelImmediately }, { headers: getAuthHeaders() }),
  changeTier: (tier) => 
    axios.post(`${API}/subscriptions/change-tier`, { tier }, { headers: getAuthHeaders() })
};

// Owner Policy API
export const ownerPolicyApi = {
  getInfo: () => axios.get(`${API}/owner-policy/info`),
  getAttestation: () => axios.get(`${API}/owner-policy/attestation`, { headers: getAuthHeaders() }),
  completeAttestation: () => 
    axios.post(`${API}/owner-policy/attestation`, { attested: true }, { headers: getAuthHeaders() }),
  reportListing: (propertyId, reason, details = null) => 
    axios.post(`${API}/properties/${propertyId}/report`, { reason, details }, { headers: getAuthHeaders() }),
  getMyReports: () => axios.get(`${API}/my-reports`, { headers: getAuthHeaders() })
};

export default {
  properties: propertiesApi,
  bookings: bookingsApi,
  messages: messagesApi,
  reviews: reviewsApi,
  payments: paymentsApi,
  saved: savedApi,
  users: usersApi,
  subscriptions: subscriptionsApi,
  ownerPolicy: ownerPolicyApi
};
